import { readTrackingStamp, isRetentionStamp } from 'components/Patients/TreatmentTracker/trackingStampUtils'

const createdAtMillis = createdAt => {
  const millis = Date.parse(createdAt)
  return Number.isNaN(millis) ? null : millis
}

// Rows with an unreadable createdAt sort to the end, equal timestamps compare as equal.
const byCreatedAt = direction => (a, b) => {
  const millisA = createdAtMillis(a.createdAt)
  const millisB = createdAtMillis(b.createdAt)

  if (millisA === millisB) {
    return 0
  }
  if (millisA === null) {
    return 1
  }
  if (millisB === null) {
    return -1
  }

  return direction === 'desc' ? millisB - millisA : millisA - millisB
}

/**
 * Rows for the admin tool's Retention reference list, newest first.
 *
 * Distinguishes the two negative answers: `[]` means the treatment genuinely has no
 * references, `null` means the stack exists but cannot be read. A corrupt stack is the
 * single most interesting state this internal tool can show, so it must not render as
 * the answer reserved for "fine and empty".
 */
export const buildRetentionReferenceRows = txTrackerRetentionStls => {
  if (!txTrackerRetentionStls) {
    return []
  }

  let stack
  try {
    stack = JSON.parse(txTrackerRetentionStls)
  } catch (ex) {
    return null
  }

  if (!stack || typeof stack !== 'object' || Array.isArray(stack)) {
    return null
  }

  if (stack.references === undefined) {
    return []
  }

  if (!stack.references || typeof stack.references !== 'object' || Array.isArray(stack.references)) {
    return null
  }

  return Object.entries(stack.references)
    .map(([referenceId, reference]) => ({
      referenceId,
      provider: reference?.provider,
      status: reference?.status,
      createdAt: reference?.createdAt,
      isActive: referenceId === stack.activeReferenceId
    }))
    .sort(byCreatedAt('desc'))
}

/**
 * The patient's Retention-stamped scans, oldest first (the same order the active list
 * renders in), each with the reference its stamp binds it to. Never throws: null
 * elements and unreadable stamps read as unstamped.
 */
export const buildRetentionScanRows = grinScans =>
  (grinScans || [])
    .filter(Boolean)
    .map(scan => ({ scan, stamp: readTrackingStamp(scan.treatmentTrackerData) }))
    .filter(({ stamp }) => isRetentionStamp(stamp))
    .map(({ scan, stamp }) => ({ scan, referenceId: stamp.referenceId }))
    .sort(({ scan: a }, { scan: b }) => byCreatedAt('asc')(a, b))

/**
 * The scans the Active Tx view lists: tracker runs that are not Retention-stamped,
 * oldest first. Before the Retention view existed the list was every scan with a
 * tracker status; now that the label says Active Tx, a retention run in it would be
 * wrong, not just unlabeled.
 */
export const buildActiveScanRows = grinScans =>
  (grinScans || [])
    .filter(Boolean)
    .filter(scan => !!scan.treatmentTrackerStatus && !isRetentionStamp(readTrackingStamp(scan.treatmentTrackerData)))
    .sort(byCreatedAt('asc'))
